import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, PencilLine, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/app/Common";
import { FitDial } from "@/components/fit/FitDial";
import { RecChip, Waterfall } from "@/components/fit/FitBits";
import { get, errMsg } from "@/lib/api";
import { stageLabel } from "@/lib/charts";
import { DECISION_LABEL } from "@/lib/fit";
import { actions } from "@/lib/candidateActions";
import { LeaningChip, WaitingOn } from "@/components/fit/Provisional";
import { CollapsibleSection } from "@/components/app/CollapsibleSection";
import { HrmsCard } from "@/pages/candidates/overview/HrmsCard";
import { ScoreOverrideDialog } from "@/pages/candidates/overview/ScoreOverrideDialog";

const Fact = ({ label, value, testId }) => <div data-testid={testId}><p className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</p><p className="mt-0.5 text-sm font-medium text-ink">{value ?? "—"}</p></div>;

function nextAction(fit, app, d) {
  if (d.decision) return d.decision.decision !== "hold" && !d.outcome_email ? ["Send outcome email", "outcome"] : ["Update decision", "decide"];
  const st = (k) => fit.components.find((c) => c.key === k)?.status;
  if (st("personality") === "missing") return ["Send assessment", "assessment"];
  if (st("work_sample") === "missing") return ["Send work sample", "work_sample"];
  if (st("interview") === "missing") return ["Prepare interview", "interview"];
  return ["Decide", "decide"];
}

function List({ title, items, testId, tone }) {
  return <section className="panel p-5" data-testid={testId}><h3 className="mb-2 text-sm font-semibold">{title}</h3>
    <ul className="space-y-1.5 text-sm text-ink-secondary">{items.length ? items.map((x) => <li key={x} className="flex gap-2"><span className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />{x}</li>) : <li className="text-ink-muted">None noted</li>}</ul></section>;
}

export function FitOverview({ app, canEdit, onDecide, onOutcome }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [ov, setOv] = useState(false);
  const key = ["app-fit", app.id];
  const { data: d, isLoading } = useQuery({ queryKey: key, queryFn: () => get(`/applications/${app.id}/fit`),
    refetchInterval: (q) => (q.state.data?.fit?.explanation_status === "queued" || q.state.data?.hrms?.status === "pending" ? 3000 : false) });
  if (isLoading) return <SkeletonRows rows={6} />;
  const fit = d.fit;
  if (!fit) return null;
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["candidate"] }); };
  const act = nextAction(fit, app, d);
  const doAct = async () => {
    if (act[1] === "decide") return onDecide();
    if (act[1] === "outcome") return onOutcome();
    try {
      if (act[1] === "assessment") await actions.sendAssessment(app);
      if (act[1] === "work_sample") await actions.sendWorkSample(app);
      if (act[1] === "interview") { await actions.prepareInterview(app); nav(`/app/candidates/${app.candidate_id}/interview`); }
      toast.success("Done"); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const f = fit.facts || {};
  const ws = fit.components.find((c) => c.key === "work_sample");
  const iv = fit.components.find((c) => c.key === "interview");
  return (
    <div className="space-y-5" data-testid={`fit-overview-${app.id}`}>
      <section className="panel grid gap-6 p-6 md:grid-cols-[auto_1fr]">
        <FitDial score={fit.fit_score} confidence={fit.confidence} provisional={fit.provisional} testId="overview-fit-dial" />
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {fit.provisional ? <LeaningChip rec={fit.recommendation} testId="overview-recommendation" /> : <RecChip rec={fit.recommendation} testId="overview-recommendation" />}
            <WaitingOn missing={fit.missing} testId="overview-waiting-on" />
            {fit.overridden && <span className="rounded-full border border-[#E8D7B0] bg-[#F3EEE3] px-2 py-0.5 text-xs font-medium text-[#7A5300]" data-testid="fit-overridden-badge">Overridden (computed {fit.fit_computed})</span>}
            {d.decision && <span className="rounded-full bg-ink px-2.5 py-0.5 text-xs font-medium text-white" data-testid="overview-decision">{DECISION_LABEL[d.decision.decision]} · {d.decision.reason_code}</span>}
            <span className="text-xs text-ink-muted">{app.job_title}</span>
          </div>
          <p className="max-w-2xl text-[15px] leading-relaxed text-ink" data-tour="pt-fit-explanation" data-testid="fit-explanation">
            {fit.explanation_status === "queued" ? <span className="flex items-center gap-2 text-ink-muted"><Loader2 size={14} className="animate-spin" /> Writing the summary…</span> : fit.explanation || "No summary yet."}
          </p>
          <p className="text-xs text-ink-muted" data-testid="fit-confidence-reasons"><Info size={12} className="mr-1 inline" />Calculated in code from {fit.confidence_reasons.join(" · ")}. The summary only restates these facts.</p>
          <div className="flex flex-wrap gap-2 pt-1">
            {act && (canEdit || d.can_decide) && <Button onClick={doAct} data-testid="overview-next-action">{act[0]} <ArrowRight size={14} /></Button>}
            {canEdit && <Button variant="outline" onClick={() => setOv(true)} data-testid="overview-override-btn"><PencilLine size={14} /> Override a score</Button>}
          </div>
        </div>
      </section>
      <div className="grid grid-cols-2 gap-4 rounded-[6px] border border-line bg-white px-6 py-4 sm:grid-cols-3 lg:grid-cols-6" data-tour="pt-key-facts" data-testid="key-facts">
        <Fact label="Stage" value={stageLabel(d.stage)} testId="fact-stage" />
        <Fact label="Must-haves" value={f.must_haves} testId="fact-must-haves" />
        <Fact label="Relevant years" value={f.relevant_years} testId="fact-years" />
        <Fact label="Assessment reliability" value={f.reliability} testId="fact-reliability" />
        <Fact label="Work sample" value={ws.status === "complete" ? `${ws.value}/100` : ws.note || "—"} testId="fact-work-sample" />
        <Fact label="Interview" value={iv.status === "complete" ? `${iv.value}/100` : iv.note || "—"} testId="fact-interview" />
      </div>
      <CollapsibleSection title="How the score adds up" subtitle="Each bar is a component's weighted contribution out of 10." testId="overview-breakdown-section">
        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr_1fr]">
          <Waterfall fit={fit} />
          <List title="Top strengths" items={fit.strengths} testId="overview-strengths" tone="bg-success" />
          <List title="Top gaps" items={fit.gaps} testId="overview-gaps" tone="bg-risk" />
        </div>
      </CollapsibleSection>
      {d.decision?.decision === "hire" && <HrmsCard appId={app.id} hrms={d.hrms} canRetry={canEdit} onChanged={refresh} />}
      {ov && <ScoreOverrideDialog appId={app.id} overrides={d.overrides} wsEnabled={app.work_sample_enabled} open={ov} onOpenChange={setOv} onChanged={refresh} />}
    </div>
  );
}
